import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
  useCallback,
} from "react";

export type Theme = {
  primary?: string;
  secondary?: string;
  third?: string;
  menu_buttons_selection?: string;
  menu_buttons_selection_effect?: string;
  [key: string]: any;
};

type ThemeContextValue = {
  theme: Theme | null;
  setTheme: (theme: Theme | null) => void;
  systemTitle: string;
  setSystemTitle: (title: string) => void;
  departmentColor: string;
  setDepartmentColor: (color: string) => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

type ThemeProviderProps = {
  children: ReactNode;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme | null>(() => {
    try {
      const raw = localStorage.getItem("eva_theme");
      return raw ? (JSON.parse(raw) as Theme) : null;
    } catch {
      return null;
    }
  });

  const [systemTitle, setSystemTitleState] = useState<string>(() => {
    try {
      const saved = localStorage.getItem("eva_system_title");
      return saved || "HAVOX";
    } catch {
      return "HAVOX";
    }
  });

  const [departmentColor, setDepartmentColorState] = useState<string>(() => {
    try {
      const saved = localStorage.getItem("eva_department_color");
      return saved || "#0a343d";
    } catch {
      return "#0a343d";
    }
  });

  const handleSetTheme = useCallback((t: Theme | null) => {
    try {
      if (t) {
        localStorage.setItem("eva_theme", JSON.stringify(t));
      } else {
        localStorage.removeItem("eva_theme");
      }
    } catch {
      /* ignore */
    }
    setThemeState(t);
  }, []);

  const handleSetSystemTitle = useCallback((title: string) => {
    try {
      localStorage.setItem("eva_system_title", title);
    } catch {
      /* ignore */
    }
    setSystemTitleState(title);
  }, []);

  const handleSetDepartmentColor = useCallback((color: string) => {
    const safeColor = color || "#0a343d";

    try {
      localStorage.setItem("eva_department_color", safeColor);
    } catch {
      /* ignore */
    }

    setDepartmentColorState(safeColor);
  }, []);

  const value = useMemo(
    () => ({
      theme,
      setTheme: handleSetTheme,
      systemTitle,
      setSystemTitle: handleSetSystemTitle,
      departmentColor,
      setDepartmentColor: handleSetDepartmentColor,
    }),
    [
      theme,
      systemTitle,
      departmentColor,
      handleSetTheme,
      handleSetSystemTitle,
      handleSetDepartmentColor,
    ]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme deve ser usado dentro de <ThemeProvider>");
  }
  return ctx;
};
